<template>
  <div v-if="account && company" class="contact-review card p-4 mb-3">
    <h3 class="mb-1 mb-sm-4">
      {{ $t("signup.contact_review_component.signer_title") }}
    </h3>
    <TextRowComponent v-bind="accountField('name')" />
    <TextRowComponent
      v-bind="accountField('phone_number')"
      type="tel"
      countryCode="FI"
    />
    <TextRowComponent v-bind="accountField('registry_code')" />

    <CompanySearchAndForm
      :hasCompanyDetails
      @quickfill-select="handleQuickFill"
    >
      <template #quickfill="{ handleSelect }">
        <CompanyQuickFill @select="handleSelect" />
      </template>
      <TextRowComponent v-bind="companyField('name')" />
      <TextRowComponent v-bind="companyField('registry_code')" />
      <TextRowComponent v-bind="companyField('vat')" />
      <TextRowComponent v-bind="companyField('address')" />
    </CompanySearchAndForm>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import {
  accountFieldBinder,
  companyFieldBinder,
  type AccountRules,
  type CompanyRules,
} from "@/signup/common/contactFields";
import TextRowComponent from "@/signup/common/TextRowComponent.vue";
import CompanySearchAndForm from "@/signup/common/CompanySearchAndForm.vue";
import CompanyQuickFill from "@/signup/fi/components/CompanyQuickFill.vue";
import { useSignupStore } from "@/signup/signup";
import type { Company } from "@/types";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Finnish contact step: the signatory with their personal ID, and the company, which can be
 * filled from a company already registered in Fingrid. Its validation is collected by the flow,
 * which saves the step.
 */
const { t } = useI18n();
const {
  required,
  personalName,
  phoneNumber,
  finnishPersonalID,
  finnishIDorBusinessID,
  isValidVAT,
} = useValidators(t);

const signupStore = useSignupStore();
const { account, company } = storeToRefs(signupStore);

const accountRules: AccountRules = {
  name: { required, personalName },
  phone_number: { required, phoneNumber },
  registry_code: { required, finnishPersonalID },
};
const companyRules: CompanyRules = {
  name: { required },
  registry_code: { required, finnishIDorBusinessID },
  vat: { required, isValidVAT },
  address: { required },
};

const accountField = accountFieldBinder(
  () => account.value,
  signupStore.updateAccount,
  () => accountRules
);
const companyField = companyFieldBinder(
  () => company.value ?? {},
  signupStore.updateCompany,
  () => companyRules
);

const hasCompanyDetails = computed(() =>
  Boolean(
    company.value?.name ||
    company.value?.registry_code ||
    company.value?.vat ||
    company.value?.address
  )
);

const handleQuickFill = (picked: Company) => {
  if (!company.value) return;
  // The search result's `id` is a registry id, not this company's: keep the local id, or the draft
  // loses track of the unsaved company and the whole contact form disappears.
  signupStore.updateCompany({ ...picked, id: company.value.id });
};
</script>
